// src/Components/ImportMarkers/DataIntegrity.js

import React, { useState, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Firestore & Storage Imports
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  updateDoc,
  deleteField,
} from 'firebase/firestore';
import { ref, listAll, deleteObject } from 'firebase/storage';
import { db, storage } from '../../Firebase/firebase';

// Import Icons
import {
  MenuDelete,
  MenuDeleteGrey,
  MenuAddNewGrey,
  MenuSaveGrey,
} from '../../assets/icons/CustomIcons';

// Import Custom Components
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

const TICK_ICON_SIZE = 31;
const TICK_MARGIN_TOP = '1px';

export default function DataIntegrity() {
  const [isScanning, setIsScanning] = useState(false);
  const [ghosts, setGhosts] = useState([]);
  const [hasScanned, setHasScanned] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const tableContainerRef = useRef(null);

  const ICON_SIZE = 64;
  const toolBlockStyle = {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    minWidth: 0,
  };

  const handleScan = useCallback(async () => {
    if (isScanning) return;
    setIsScanning(true);
    setSelectedIds([]);
    const foundGhosts = [];

    try {
      // AMENDED: Included newsPortfolio collection in the global scan
      const [groupsSnap, markersSnap, polygonsSnap, polylinesSnap, newsPortfolioSnap] =
        await Promise.all([
          getDocs(collection(db, 'markerGroups')),
          getDocs(collection(db, 'markers')),
          getDocs(collection(db, 'polygons')),
          getDocs(collection(db, 'polylines')),
          getDocs(collection(db, 'newsPortfolio')),
        ]);

      const groupsMap = new Map();
      const allDbData = [];

      groupsSnap.forEach((docSnap) => {
        const data = docSnap.data();
        groupsMap.set(docSnap.id, data);
        allDbData.push(data);
      });

      const geometryMaps = {
        marker: new Map(),
        polygon: new Map(),
        line: new Map(),
      };

      markersSnap.forEach((docSnap) => {
        const data = docSnap.data();
        geometryMaps.marker.set(docSnap.id, data);
        allDbData.push(data);
      });
      polygonsSnap.forEach((docSnap) => {
        const data = docSnap.data();
        geometryMaps.polygon.set(docSnap.id, data);
        allDbData.push(data);
      });
      polylinesSnap.forEach((docSnap) => {
        const data = docSnap.data();
        geometryMaps.line.set(docSnap.id, data);
        allDbData.push(data);
      });

      // Push newsPortfolio into the stringifier array so its active Storage paths are recognized
      newsPortfolioSnap.forEach((docSnap) => {
        allDbData.push(docSnap.data());
      });

      const dbStringified = JSON.stringify(allDbData);

      // --- PHASE 2: DATABASE BIDIRECTIONAL CHECK ---
      groupsMap.forEach((data, groupId) => {
        let isGhost = false;
        let missingLinks = [];

        if (data.markerID && !geometryMaps.marker.has(data.markerID)) {
          isGhost = true;
          missingLinks.push(`Marker: ${data.markerID}`);
        }
        if (data.polygonID && !geometryMaps.polygon.has(data.polygonID)) {
          isGhost = true;
          missingLinks.push(`Polygon: ${data.polygonID}`);
        }
        if (data.polylineID && !geometryMaps.line.has(data.polylineID)) {
          isGhost = true;
          missingLinks.push(`Polyline: ${data.polylineID}`);
        }

        if (!data.markerID && !data.polygonID && !data.polylineID) {
          isGhost = true;
          missingLinks.push('No shapes attached (Empty Group)');
        }

        if (isGhost) {
          foundGhosts.push({
            id: groupId,
            collection: 'markerGroups',
            type: 'Unlinked Group',
            title: data.title || data.companyName || data.address || 'Unnamed Location',
            issue: missingLinks.join(', '),
            isStorage: false,
            rawData: data,
          });
        }
      });

      const checkGeometryForGhosts = (map, typeName, collectionName) => {
        map.forEach((data, geomId) => {
          const targetGroupId = data.markerGroupID || geomId;
          if (!groupsMap.has(targetGroupId)) {
            foundGhosts.push({
              id: geomId,
              collection: collectionName,
              type: `Unlinked ${typeName}`,
              title: `Unlinked ${typeName}`,
              issue: `Missing Group: ${targetGroupId}`,
              isStorage: false,
              rawData: data,
            });
          }
        });
      };

      checkGeometryForGhosts(geometryMaps.marker, 'Marker', 'markers');
      checkGeometryForGhosts(geometryMaps.polygon, 'Polygon', 'polygons');
      checkGeometryForGhosts(geometryMaps.line, 'Polyline', 'polylines');

      // --- PHASE 3: STORAGE INTEGRITY CHECK ---
      // AMENDED: Included promoDocuments folder
      const foldersToScan = ['documents', 'marker_images', 'photos', 'promoDocuments'];
      const validStoragePaths = new Set();

      const getAllFilesInFolder = async (folderRef, currentDepth = 0) => {
        if (currentDepth > 4) return [];

        let files = [];
        try {
          const res = await listAll(folderRef);
          files.push(...res.items);

          const subfolderPromises = res.prefixes.map((prefixRef) =>
            getAllFilesInFolder(prefixRef, currentDepth + 1),
          );

          const subfolderResults = await Promise.all(subfolderPromises);
          subfolderResults.forEach((subFiles) => files.push(...subFiles));
        } catch (err) {
          console.warn(`Skipping unreadable folder: ${folderRef.fullPath}`, err);
        }
        return files;
      };

      for (const folder of foldersToScan) {
        try {
          const folderRef = ref(storage, folder);
          const allFiles = await getAllFilesInFolder(folderRef);

          for (const fileRef of allFiles) {
            validStoragePaths.add(fileRef.fullPath);

            const encodedPath = encodeURIComponent(fileRef.fullPath);
            const rawName = fileRef.name;

            if (
              !dbStringified.includes(encodedPath) &&
              !dbStringified.includes(rawName)
            ) {
              foundGhosts.push({
                id: fileRef.fullPath,
                collection: folder,
                type: 'Orphaned File',
                title: fileRef.name,
                issue: `Found in Storage (/${folder}) but not referenced in Database`,
                isStorage: true,
              });
            }
          }
        } catch (storageError) {
          console.warn(
            `Data Integrity: Could not scan Storage folder '/${folder}'. It may be empty or missing permissions.`,
            storageError,
          );
        }
      }

      // --- PHASE 4: Check for dead storage links in newsPortfolio ---
      newsPortfolioSnap.forEach((docSnap) => {
        const data = docSnap.data();
        const missingRefs = [];

        if (data.pdfStoragePath && !validStoragePaths.has(data.pdfStoragePath)) {
          missingRefs.push('PDF');
        }
        if (data.thumbStoragePath && !validStoragePaths.has(data.thumbStoragePath)) {
          missingRefs.push('Thumbnail');
        }
        if (data.hiresStoragePath && !validStoragePaths.has(data.hiresStoragePath)) {
          missingRefs.push('Hi-Res Image');
        }

        if (missingRefs.length > 0) {
          foundGhosts.push({
            id: docSnap.id,
            collection: 'newsPortfolio',
            type: 'Dead Reference',
            title: data.name || data.title || 'Unnamed Newsfeed',
            issue: `References missing storage files: ${missingRefs.join(', ')}`,
            isStorage: false,
            rawData: data,
          });
        }
      });

      setGhosts(foundGhosts);
      setHasScanned(true);
    } catch (error) {
      console.error('Error during integrity scan:', error);
    } finally {
      setIsScanning(false);
    }
  }, [isScanning]);

  const handleCheckboxChange = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((item) => item !== id);
      }
      return [...prev, id];
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(ghosts.map((g) => g.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleDeleteRequest = () => {
    if (selectedIds.length > 0) {
      setShowDeleteConfirm(true);
    }
  };

  const executeDelete = async () => {
    setShowDeleteConfirm(false);
    try {
      const targets = ghosts.filter((g) => selectedIds.includes(g.id));

      const dbTargets = targets.filter((t) => !t.isStorage);
      const storageTargets = targets.filter((t) => t.isStorage);

      const dbPromises = dbTargets.map(async (target) => {
        if (target.collection === 'markerGroups' && target.rawData) {
          const groupData = target.rawData;
          const urlsToDelete = [];

          if (groupData.hoverImage) urlsToDelete.push(groupData.hoverImage);

          if (groupData.photos && Array.isArray(groupData.photos)) {
            groupData.photos.forEach((photo) => {
              if (photo.url) urlsToDelete.push(photo.url);
            });
          }

          if (groupData.documents && Array.isArray(groupData.documents)) {
            groupData.documents.forEach((docItem) => {
              if (docItem.url) urlsToDelete.push(docItem.url);
            });
          }

          for (const url of urlsToDelete) {
            if (url.includes('firebasestorage')) {
              try {
                await deleteObject(ref(storage, url));
              } catch (err) {
                if (err.code !== 'storage/object-not-found') {
                  console.warn(`Error deleting file from storage: ${url}`, err);
                }
              }
            }
          }
        }

        // AMENDED: Surgically clear dead references without destroying the actual Newsfeed document
        if (target.collection === 'newsPortfolio' && target.type === 'Dead Reference') {
          return updateDoc(doc(db, target.collection, target.id), {
            pdfDownloadUrl: deleteField(),
            pdfThumbnailUrl: deleteField(),
            hiresDownloadUrl: deleteField(),
            pdfStoragePath: deleteField(),
            thumbStoragePath: deleteField(),
            hiresStoragePath: deleteField(),
            emailPreviewHtml: deleteField(),
          });
        }

        return deleteDoc(doc(db, target.collection, target.id));
      });

      const storagePromises = storageTargets.map((target) =>
        deleteObject(ref(storage, target.id)),
      );

      await Promise.all([...dbPromises, ...storagePromises]);

      setGhosts((prev) => prev.filter((g) => !selectedIds.includes(g.id)));
      setSelectedIds([]);
    } catch (err) {
      console.error('Error deleting affected documents or files:', err);
    }
  };

  const canDelete = selectedIds.length > 0;
  const isAllSelected = ghosts.length > 0 && selectedIds.length === ghosts.length;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          height: '53px',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#f4f4f4',
          borderBottom: '1px solid #ccc',
          flexShrink: 0,
          zIndex: 50,
          px: '5px',
          mb: 2,
        }}>
        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuAddNewGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} disabled={true}>
            <MenuSaveGrey sx={{ fontSize: ICON_SIZE }} />
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton
            sx={{ p: 0, transform: 'translateY(-5px)' }}
            onClick={handleDeleteRequest}
            disabled={!canDelete}>
            {canDelete ? (
              <MenuDelete sx={{ fontSize: ICON_SIZE }} />
            ) : (
              <MenuDeleteGrey sx={{ fontSize: ICON_SIZE }} />
            )}
          </IconButton>
        </Box>

        <Box sx={toolBlockStyle}>
          <IconButton sx={{ p: 0, transform: 'translateY(-5px)' }} onClick={handleScan}>
            {isScanning ? (
              <CircularProgress sx={{ color: '#4caf50', mt: TICK_MARGIN_TOP }} />
            ) : (
              <CheckCircleIcon
                sx={{ fontSize: TICK_ICON_SIZE, color: '#4caf50', mt: TICK_MARGIN_TOP }}
              />
            )}
          </IconButton>
        </Box>
      </Box>

      {!hasScanned && !isScanning ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography
            variant='body1'
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            Click the green tick button above to scan the database for integrity.
          </Typography>
        </Box>
      ) : hasScanned && ghosts.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography variant='body1' sx={{ color: 'black', fontWeight: 'normal' }}>
            Database integrity is confirmed.
          </Typography>
        </Box>
      ) : (
        <TableContainer
          ref={tableContainerRef}
          component={Paper}
          elevation={0}
          sx={{
            flexGrow: 1,
            width: '100%',
            boxSizing: 'border-box',
            borderTop: '2px solid #0055ff',
            borderBottom: 'none',
            borderLeft: 'none',
            borderRight: 'none',
            borderRadius: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}>
          <Table
            stickyHeader
            size='small'
            aria-label='integrity table'
            sx={{ width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  height: '36px',
                  '& th': {
                    backgroundColor: '#e3f2fd',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #0055ff',
                    py: '4px',
                  },
                }}>
                <TableCell padding='checkbox' sx={{ width: '50px' }}>
                  <Checkbox
                    size='small'
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    sx={{ p: 0.5 }}
                  />
                </TableCell>
                <TableCell sx={{ width: '20%' }}>Type</TableCell>
                <TableCell sx={{ width: '30%' }}>Title</TableCell>
                <TableCell sx={{ width: '25%' }}>Document/File ID</TableCell>
                <TableCell sx={{ width: '25%' }}>Issue</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {ghosts.map((row) => {
                const isSelected = selectedIds.includes(row.id);

                let typeColor = '#ed6c02';
                if (row.type.includes('Group')) typeColor = '#d32f2f';
                if (row.isStorage) typeColor = '#9c27b0';
                if (row.type === 'Dead Reference') typeColor = '#d32f2f';

                return (
                  <TableRow
                    key={row.id}
                    hover
                    selected={isSelected}
                    onClick={() => handleCheckboxChange(row.id)}
                    sx={{
                      cursor: 'pointer',
                      height: '32px',
                      '& td': { py: '4px', fontSize: '0.8rem' },
                      '&.Mui-selected': { backgroundColor: 'transparent !important' },
                      '&.Mui-selected:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                      },
                    }}>
                    <TableCell padding='checkbox'>
                      <Checkbox
                        size='small'
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => handleCheckboxChange(row.id)}
                        sx={{ p: 0.5 }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: typeColor, fontWeight: 'bold' }}>
                      {row.type}
                    </TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                      {row.id}
                    </TableCell>
                    <TableCell>{row.issue}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={executeDelete}
        message={`This will fix or permanently delete ${selectedIds.length} affected record(s) or file(s). Are you sure?`}
        severity='error'
        confirmText='Yes'
        cancelText='No'
        disableRestoreFocus
        sx={{ zIndex: 100005 }}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, 0.1)' },
        }}
      />
    </Box>
  );
}
